import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Star, ArrowLeft, ArrowRight } from 'lucide-react';

/**
 * Compact preview of the Golden Oz artwork shown on the homepage.
 * Links to the dedicated /golden-oz page for the full experience.
 * This avoids duplicate content while keeping the artwork visible on the main flow.
 */
export const GoldenOzPreview = () => {
  const { isRTL } = useLanguage();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  const t = isRTL
    ? {
        eyebrow: 'יצירה מרכזית',
        title: 'גולדן עוז',
        titleEn: 'Golden Oz',
        location: 'נחל עוז',
        tagline: 'מיצב מינרלי לזכר 7.10',
        intro:
          'מיצב תלת־ממדי בציפוי מיקרומינרל, מתוכנן לפי יחס הזהב (1:1.618). מקצב של 71.0 מ"מ חוזר ביצירה כסמל ליום ה־7.10, לזכר הנופלים הגיבורים מנחל עוז.',
        cta: 'צפו בעמוד היצירה המלא',
        imageAlt:
          'גולדן עוז - מיצב אומנותי מינרלי לזכר 7.10 בנחל עוז',
      }
    : {
        eyebrow: 'Featured Artwork',
        title: 'Golden Oz',
        titleEn: '',
        location: 'Nahal Oz',
        tagline: 'Mineral installation in memory of October 7',
        intro:
          'A 3D installation in microMineral coating, designed by the golden ratio (1:1.618). A 71.0 mm rhythm recurs throughout as a symbol of October 7, in memory of the fallen heroes of Nahal Oz.',
        cta: 'View the full artwork page',
        imageAlt:
          'Golden Oz - mineral memorial art installation for October 7 at Nahal Oz',
      };

  return (
    <section
      id="golden-oz-section"
      data-testid="golden-oz-preview"
      className="relative py-12 md:py-16 px-4 overflow-hidden"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Earth-gold background */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#FAF6EE] via-[#F5EBD7] to-[#EFE0C2]" />
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 0.08, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.2 }}
        className="pointer-events-none absolute -top-20 -right-20 w-[320px] h-[320px] -z-10"
      >
        <div
          className="w-full h-full"
          style={{
            clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
            background: 'linear-gradient(180deg, #E5B53A 0%, #D4A84B 100%)',
          }}
        />
      </motion.div>

      <div className="max-w-5xl mx-auto">
        <Link
          to="/golden-oz"
          data-testid="golden-oz-preview-link"
          className="group block"
          aria-label={t.cta}
        >
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="grid md:grid-cols-2 gap-6 md:gap-10 items-center bg-white/60 backdrop-blur-sm rounded-2xl border border-[#B8862C]/20 p-5 sm:p-6 md:p-8 shadow-md hover:shadow-xl transition-shadow"
          >
            {/* Image side */}
            <div className="relative rounded-xl overflow-hidden border border-[#B8862C]/15 shadow-inner order-1 md:order-2">
              <img
                src="/images/golden_oz.jpg"
                alt={t.imageAlt}
                className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.03]"
                loading="lazy"
                width="1524"
                height="704"
                data-testid="golden-oz-preview-image"
              />
            </div>

            {/* Text side */}
            <div className={`order-2 md:order-1 ${isRTL ? 'md:text-right' : 'md:text-left'} text-center md:text-start`}>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E5B53A]/15 border border-[#E5B53A]/30 mb-4">
                <Star className="w-3.5 h-3.5 text-[#B8862C] fill-[#E5B53A]" />
                <span className="text-xs font-medium tracking-widest uppercase text-[#8C661F]">
                  {t.eyebrow}
                </span>
              </div>

              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#3A2E18] mb-2 tracking-tight leading-tight">
                {t.title}
                {t.titleEn && (
                  <span className="block sm:inline text-[#B8862C] sm:ms-2 mt-1 sm:mt-0 text-2xl sm:text-3xl lg:text-4xl">
                    — {t.titleEn}
                  </span>
                )}
              </h2>

              <p className="text-sm text-[#6B5A3A] mb-3">
                <span className="font-medium">{t.location}</span>
                <span className="mx-2 text-[#B8862C]/50">•</span>
                <span className="italic">{t.tagline}</span>
              </p>

              <p className="text-base text-[#4A3E22] leading-relaxed mb-5">
                {t.intro}
              </p>

              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#3A2E18] text-[#FAF6EE] text-sm font-medium tracking-wide shadow-md group-hover:bg-[#5C4823] transition-colors">
                <span>{t.cta}</span>
                <Arrow className="w-4 h-4 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" />
              </div>
            </div>
          </motion.div>
        </Link>
      </div>
    </section>
  );
};

export default GoldenOzPreview;
